import { zodResolver } from '@hookform/resolvers/zod';
import { useState, type FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import {
  Button,
  LoadingOverlay,
  PhoneNumberInput,
  ResponsiveModalBottomSheet,
} from '@/components';
import type { PhoneNumberInputValue } from '@/components/PhoneNumberInput';
import { BookingService } from '@/services/bookingService';
import { phoneSuperRefine } from '@/utils/validation';

interface OtpNavState {
  ref: string;
  phoneNumber: string;
  telPrefix: string;
  tel: string;
  devOtpCode?: string;
  expiresAt: Date;
}

const phoneInputSchema = z
  .object({
    tel: z.string().optional(),
    telPrefix: z.string().optional(),
  })
  .superRefine(phoneSuperRefine('tel', 'telPrefix', '+66'));

type PhoneInputFormData = z.infer<typeof phoneInputSchema>;

const PhoneNumberInputPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [showGuestBookingSheet, setShowGuestBookingSheet] = useState(false);

  const {
    trigger,
    getValues,
    formState: { errors },
    watch,
    setValue,
  } = useForm<PhoneInputFormData>({
    resolver: zodResolver(phoneInputSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      telPrefix: '+66',
      tel: '',
    },
  });

  const handleContinueAsGuest = () => {
    setShowGuestBookingSheet(false);
    void navigate('/booking/select-date-time');
  };

  const handleSendOtp = async () => {
    const isPhoneValid = await trigger(['tel', 'telPrefix']);
    if (!isPhoneValid) {
      return;
    }

    const formData = getValues();
    const telPrefix = formData.telPrefix || '+66';
    const tel = formData.tel || '';
    const phoneNumberE164 = `${telPrefix}${tel}`;

    if (telPrefix !== '+66') {
      setErrorKey(null);
      setShowGuestBookingSheet(true);
      return;
    }

    setLoading(true);
    setErrorKey(null);
    try {
      const exists = await BookingService.checkAuthExists(telPrefix, tel);

      if (!exists) {
        navigate('/profile/settings', {
          state: {
            telPrefix,
            tel,
          },
        });
        return;
      }

      const resp = await BookingService.requestOTP(
        phoneNumberE164,
        'phoneNumber',
      );

      const state: OtpNavState = {
        ref: resp.ref,
        phoneNumber: phoneNumberE164,
        telPrefix,
        tel,
        devOtpCode: resp.code,
        expiresAt: resp.expiresAt,
      };

      void navigate('/profile/otp', { state });
    } catch (e) {
      console.error('Failed to request OTP:', e);
      setErrorKey('profile.phoneInput.errors.sendOtpFailed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-md flex-1 flex-col">
      <div className="flex flex-1 flex-col justify-between">
        <div className="flex flex-col items-center">
          <img
            src="/illustrations/phone-input.png"
            alt="phone input illustration"
            className="mt-2 mb-6 h-40 w-52"
          />

          <div className="w-full space-y-6">
            <div className="space-y-2 text-center">
              <h2 className="text-primary-text text-xl leading-7 font-semibold">
                {t('profile.phoneInput.title')}
              </h2>
              <p className="text-secondary-text text-base leading-6">
                {t('profile.phoneInput.descriptionLine1')}
                <br />
                {t('profile.phoneInput.descriptionLine2')}
              </p>
            </div>

            <PhoneNumberInput
              telPrefix={watch('telPrefix') || '+66'}
              value={watch('tel') || ''}
              label=""
              placeholder=""
              aria-label="phone number"
              onCountryChange={(country) => {
                const nextDialCode = `+${country.dialCode}`;
                setShowGuestBookingSheet(nextDialCode !== '+66');
                if (errorKey) {
                  setErrorKey(null);
                }
              }}
              onChange={(v: PhoneNumberInputValue) => {
                setValue('telPrefix', v.dialCode, {
                  shouldValidate: false,
                  shouldDirty: true,
                });
                setValue('tel', v.national, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                if (errorKey) {
                  setErrorKey(null);
                }
              }}
              error={errors.tel?.message ? t(errors.tel.message) : undefined}
            />

            {errorKey && (
              <p className="text-error text-sm leading-5">{t(errorKey)}</p>
            )}
          </div>
        </div>

        <div className="w-full pt-10">
          <div className="space-y-4">
            <Button type="button" onClick={handleSendOtp} disabled={loading}>
              {t('profile.phoneInput.sendOtp')}
            </Button>
          </div>
        </div>
      </div>

      <LoadingOverlay isVisible={loading} />

      <ResponsiveModalBottomSheet
        isOpen={showGuestBookingSheet}
        onClose={() => setShowGuestBookingSheet(false)}
        showCloseOnMobile={false}
        closable={true}
      >
        <div className="flex flex-col gap-4 px-4 pb-6">
          <div className="flex flex-col gap-2 text-center">
            <p className="text-xl leading-7 font-semibold text-black">
              {t('profile.phoneInput.guestModal.title')}
            </p>
            <p className="text-secondary-text text-xl leading-7 font-semibold">
              {t('profile.phoneInput.guestModal.description')}
            </p>
          </div>
          <Button type="button" onClick={handleContinueAsGuest}>
            {t('profile.phoneInput.guestModal.bookAsGuest')}
          </Button>
        </div>
      </ResponsiveModalBottomSheet>
    </div>
  );
};

export default PhoneNumberInputPage;
